// The API refusing the session (v1.3 ticket 10, RCMC-22).
//
// One response interceptor on the shared axios instance, which every screen uses, added once when this module
// loads, so it is in place before any screen sends a request (axios applies the interceptors present when a
// request is sent). When the API answers 401 or 403 to a request that carried a session token, it hands that
// token to the handler AuthContextProvider sets. If the handler ends the session (or it had already ended), the
// request's own promise never settles: the screen that sent it is being replaced by the sign-in screen, so its
// error handling must not run (it would put "HTTP 401" or token jargon on top of the expired-session message).
// v1.2 had each screen send the page to /login itself, leaving the tokens in storage and saying nothing.
//
// A request sent without a session token (sign-in's own steps) passes through untouched: a 403 there (e.g.
// Django's CSRF check) is not a session ending.
import axios from "axios";

let onSessionRefused = null;

/**
 * Set what happens when the API refuses a session token. Only one handler at a time (the app has one
 * AuthContextProvider).
 * @param {(token: string) => boolean} handler  gets the refused token; returns true if it dealt with the refusal
 *   (the session ended, or had already ended), false to let the screen handle the error as any other
 * @returns {() => void} unsets the handler (for an effect cleanup)
 */
export function handleSessionRefusal(handler) {
  onSessionRefused = handler;
  return () => {
    if (onSessionRefused === handler) onSessionRefused = null;
  };
}

/** The session token a refused request carried (401 / 403 to a `Bearer` request), else null */
export function refusedSessionToken(error) {
  const status = error?.response?.status;
  if (status !== 401 && status !== 403) return null;
  const headers = error?.config?.headers;
  const authorization = headers?.Authorization ?? headers?.authorization;
  if (typeof authorization !== "string" || !authorization.startsWith("Bearer ")) return null;
  return authorization.slice("Bearer ".length);
}

axios.interceptors.response.use(undefined, (error) => {
  const token = refusedSessionToken(error);
  if (token !== null && onSessionRefused && onSessionRefused(token)) {
    return new Promise(() => {}); // never settles: see above
  }
  return Promise.reject(error);
});
